import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { transcriptService, TranscriptData } from '../services/transcriptService';
import { useToast } from '../components/Toast';
import './TranscriptPage.css';

// Group transcript items into time blocks
const groupTranscriptIntoBlocks = (transcript: Array<{ text: string; start: number; duration?: number; dur?: number }>): Array<{ startTime: number; text: string }> => {
  if (!transcript || transcript.length === 0) return [];


  type BlockType = { startTime: number; text: string };
  const blocks: BlockType[] = [];
  let currentBlock: BlockType | null = null;
  const BLOCK_INTERVAL = 30; // Group items every 30 seconds
  const MAX_GAP = 20; // If gap between consecutive items > 20 seconds, create new block
  let skippedCount = 0;
  let lastItemStart: number | null = null;

  // Process all items - ensure we don't skip any
  transcript.forEach((item, index) => {
    const start = parseFloat(String(item.start || 0));
    const text = String(item.text || '').trim();

    // Skip only if text is truly empty (null, undefined, or empty string after trim)
    if (text === null || text === undefined || text.length === 0) {
      skippedCount++;
      return; // Skip empty text
    }

    // Log if we encounter invalid start time
    if (isNaN(start)) {
      console.warn(`⚠️ Invalid start time at index ${index}:`, item);
    }

    // Start new block if:
    // 1. First item (no current block)
    // 2. Time gap from current block start >= BLOCK_INTERVAL
    // 3. Gap from last item > MAX_GAP (significant gap between consecutive items)
    if (!currentBlock) {
      // First item - start new block
      currentBlock = {
        startTime: start,
        text: text
      };
      lastItemStart = start;
    } else {
      const currentBlockTime = (currentBlock as BlockType).startTime;
      const timeDiffFromBlockStart = Math.abs(start - currentBlockTime);
      const gapFromLastItem = lastItemStart !== null ? (start - lastItemStart) : 0;

      // Create new block if:
      // - Time from block start >= BLOCK_INTERVAL, OR
      // - Gap from last item > MAX_GAP (even if within BLOCK_INTERVAL from block start)
      // BUT: Only create new block if gap is significant (>= 15 seconds) to avoid splitting too much
      const shouldCreateNewBlock = timeDiffFromBlockStart >= BLOCK_INTERVAL || (gapFromLastItem > MAX_GAP && gapFromLastItem >= 15);

      if (shouldCreateNewBlock) {
        // Save previous block - always save if it has any content
        const blockText = (currentBlock as BlockType).text.trim();
        if (blockText.length > 0) {
          blocks.push({ ...(currentBlock as BlockType) }); // Create a copy
        }
        // Start new block
        currentBlock = {
          startTime: start,
          text: text
        };
        lastItemStart = start;
      } else {
        // Add to current block - ensure we don't lose any text
        const block = currentBlock as BlockType;
        const blockText = block.text.trim();
        if (blockText.length > 0) {
          block.text += ' ' + text;
        } else {
          block.text = text;
        }
        lastItemStart = start;
      }
    }
  });

  // Add last block - ensure we don't lose it
  if (currentBlock !== null && currentBlock !== undefined) {
    const block = currentBlock as BlockType;
    const blockText = block.text.trim();
    if (blockText.length > 0) {
      blocks.push(block);
    }
  }

  // Debug: Verify we have all items
  const totalTextLength = blocks.reduce((sum, block) => sum + (block.text ? block.text.length : 0), 0);
  const originalTextLength = transcript.reduce((sum, item) => {
    const itemText = String(item.text || '').trim();
    return sum + (itemText && itemText.length > 0 ? itemText.length : 0);
  }, 0);

  const processedItems = transcript.length - skippedCount;

  return blocks;
};

// Transform API data to component format
const transformTranscriptData = (data: any, videoId?: string): TranscriptData => {
  // Priority 1: If we have tracks with transcript array (most detailed format)
  if (data.tracks && Array.isArray(data.tracks) && data.tracks.length > 0) {
    const track = data.tracks[0];
    if (track.transcript && Array.isArray(track.transcript) && track.transcript.length > 0) {
      // Convert all transcript items, ensuring we parse numbers correctly
      // IMPORTANT: Don't filter out items - keep ALL items even if text seems empty
      // Some items might have whitespace or special characters that are valid
      const transcriptArray = track.transcript
        .map((item: any, index: number) => {
          const text = String(item.text || '').trim();
          const start = parseFloat(String(item.start || 0));
          const duration = parseFloat(String(item.dur || item.duration || 0));

          // Skip invalid start times
          if (isNaN(start)) {
            return null;
          }

          return {
            text: text,
            start: start,
            duration: duration,
            dur: duration,
          };
        })
        // Filter out null items and items that are completely empty (no text at all)
        .filter((item: any) => item !== null && item !== undefined && item.text !== null && item.text !== undefined);

      return {
        id: data.id || videoId || '',
        title: data.title || 'Untitled Video',
        video_id: videoId || data.id || '',
        transcript: transcriptArray,
        text: data.text, // Keep original text as well
        tracks: data.tracks,
      };
    }
  }

  // Priority 2: If transcript is already in array format
  if (Array.isArray(data.transcript) && data.transcript.length > 0) {
    return {
      ...data,
      video_id: videoId || data.id || '',
      transcript: data.transcript.map((item: any) => ({
        text: item.text || item,
        start: item.start || item.offset || 0,
        duration: item.duration || item.dur || 0,
      })),
    };
  }

  // Priority 3: If we have text as string, keep it as string
  if (data.text && typeof data.text === 'string') {
    return {
      id: data.id || videoId || '',
      title: data.title || 'Untitled Video',
      video_id: videoId || data.id || '',
      text: data.text,
      transcript: data.text, // Also set as transcript for compatibility
    };
  }

  // Priority 4: If transcript is a string
  if (typeof data.transcript === 'string') {
    return {
      ...data,
      video_id: videoId || data.id || '',
      text: data.transcript,
    };
  }

  // Fallback: return as is with video_id
  return {
    ...data,
    video_id: videoId || data.id || '',
    transcript: data.transcript || [],
  };
};

const TranscriptPage: React.FC = () => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [transcriptData, setTranscriptData] = useState<TranscriptData | null>(null);

  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!url.trim()) {
      showToast(t('transcript.errors.enterUrl'), 'warning');
      return;
    }

    setLoading(true);
    setTranscriptData(null);

    try {
      const result = await transcriptService.getTranscript(url);
      if (result.success && result.data) {
        // Transform data to match component expectations
        const transformedData = transformTranscriptData(result.data, result.video_id);
        setTranscriptData(transformedData);
        showToast(t('transcript.toasts.success'), 'success');
      } else {
        showToast(t('transcript.errors.noTranscript'), 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.details || t('transcript.errors.getError');
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setUrl('');
    setTranscriptData(null);
  };

  const handleCopy = () => {
    if (!transcriptData) return;

    let text = '';
    // Try different formats
    if (Array.isArray(transcriptData.transcript)) {
      text = transcriptData.transcript.map((item: any) => item.text || item).join(' ');
    } else if (typeof transcriptData.transcript === 'string') {
      text = transcriptData.transcript;
    } else if (transcriptData.text) {
      text = transcriptData.text;
    } else if (transcriptData.tracks && transcriptData.tracks.length > 0 && transcriptData.tracks[0].transcript) {
      text = transcriptData.tracks[0].transcript.map((item: any) => item.text).join(' ');
    }

    if (text && text.trim()) {
      navigator.clipboard.writeText(text);
      showToast(t('transcript.toasts.copied'), 'success');
    } else {
      showToast(t('transcript.toasts.nothingToCopy'), 'warning');
    }
  };

  return (
    <div className="transcript-page">
      <div className="transcript-page-container">
        {/* Hero Section */}
        <div className="transcript-hero">
          <div className="transcript-hero-content">
            <div className="transcript-hero-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </div>
            <h1 className="transcript-hero-title">{t('transcript.pageTitle')}</h1>
            <p className="transcript-hero-subtitle">
              {t('transcript.pageSubtitle')}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="transcript-main">
          <div className="transcript-card">
            <form onSubmit={handleSubmit} className="transcript-form">
              <div className="transcript-input-group">
                <label htmlFor="youtube-url" className="transcript-label">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {t('transcript.inputLabel')}
                </label>
                <div className="transcript-input-container">
                  <input
                    id="youtube-url"
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder={t('transcript.inputPlaceholder')}
                    className="transcript-input"
                    disabled={loading}
                    required
                  />
                  {url && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="transcript-input-clear"
                      aria-label={t('transcript.clear')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <button
                type="submit"
                className="transcript-submit-btn"
                disabled={loading || !url.trim()}
              >
                {loading ? (
                  <>
                    <div className="transcript-spinner"></div>
                    <span>{t('transcript.processing')}</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>{t('transcript.getTranscript')}</span>
                  </>
                )}
              </button>
            </form>

            {transcriptData && (
              <div className="transcript-result">
                <div className="transcript-result-header">
                  <div className="transcript-result-header-content">
                    <div className="transcript-result-badge">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t('transcript.success')}
                    </div>
                    <h3 className="transcript-result-title">{transcriptData.title || 'Untitled Video'}</h3>
                    <div className="transcript-result-meta">
                      <span className="transcript-result-meta-item">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {transcriptData.video_id || transcriptData.id}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="transcript-content">
                  {(() => {
                    // Handle array format - Group by time blocks
                    if (Array.isArray(transcriptData.transcript) && transcriptData.transcript.length > 0) {
                      // Group transcript items into blocks (every ~30 seconds or when there's a significant gap)
                      const blocks = groupTranscriptIntoBlocks(transcriptData.transcript);

                      return (
                        <div className="transcript-blocks">
                          {blocks.map((block: any, blockIndex: number) => {
                            const minutes = Math.floor(block.startTime / 60);
                            const seconds = Math.floor(block.startTime % 60);
                            const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

                            return (
                              <div key={blockIndex} className="transcript-block">
                                <div className="transcript-block-time">{timeString}</div>
                                <div className="transcript-block-text">
                                  {block.text}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    // Handle string format - Split into paragraphs or show as is
                    if (typeof transcriptData.transcript === 'string' && transcriptData.transcript.trim()) {
                      return (
                        <div className="transcript-text-simple">
                          <div className="transcript-text-content">
                            {transcriptData.transcript}
                          </div>
                        </div>
                      );
                    }

                    // Handle data.text directly
                    if (transcriptData.text && transcriptData.text.trim()) {
                      return (
                        <div className="transcript-text-simple">
                          <div className="transcript-text-content">
                            {transcriptData.text}
                          </div>
                        </div>
                      );
                    }

                    // No transcript available
                    return (
                      <p className="transcript-empty">{t('transcript.noTranscript')}</p>
                    );
                  })()}
                </div>

                <div className="transcript-actions">
                  <button
                    onClick={handleCopy}
                    className="transcript-copy-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>{t('transcript.copyTranscript')}</span>
                  </button>
                  <a
                    href={`https://www.youtube.com/watch?v=${transcriptData.video_id || transcriptData.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transcript-view-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span>{t('transcript.watchVideo')}</span>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptPage;
